import { NAV_HUB_CARD_IMAGES } from './navHubCardImages';
import {
  IPE_UPLOAD_FORMAT_HINT,
  IPE_UPLOAD_FORMAT_HINT_WITH_DIMENSION,
} from '@/utils/ipeImageUploadFormats';

/** null = 回落到会话上限 500（见 uploadSessionLimits / studioSourceLimit） */
export const IMAGE_PROCESS_MAX_SOURCES = null;

const SHARED_SPEC_ITEMS = [
  IPE_UPLOAD_FORMAT_HINT_WITH_DIMENSION,
  '可批量拖拽或粘贴上传，任务将排队处理',
  '支持选择或拖拽单个文件夹（仅读取根目录图片，最多 500 张）',
];

/** 智能抠图模式：基础=阿里云分割；高级=千问白底 + BRIA RMBG-1.4 去白底 */
export const SMART_CUTOUT_MODE_OPTIONS = [
  {
    value: 'basic',
    label: '基础',
    hint: '',
  },
  {
    value: 'advanced',
    label: '高级',
    hint: '',
  },
];

export const SMART_CUTOUT_DEFAULT_PARAMS = {
  formats: ['png'],
  mode: 'basic',
};

export function normalizeSmartCutoutFormats(raw) {
  const list = Array.isArray(raw) ? raw : SMART_CUTOUT_DEFAULT_PARAMS.formats;
  const normalized = list.map((item) => String(item || '').trim().toLowerCase()).filter(Boolean);
  return normalized.length > 0 ? normalized : ['png'];
}

export function normalizeSmartCutoutMode(raw) {
  return String(raw || '').trim().toLowerCase() === 'advanced' ? 'advanced' : 'basic';
}

/** @deprecated 保留兼容旧任务数据 */
export const SMART_CUTOUT_ENGINE_OPTIONS = [
  { value: 'ipe', label: '自研引擎' },
];

export function normalizeSmartCutoutEngine() {
  return 'ipe';
}

export function isSmartCutoutQwenEngine(mode) {
  return normalizeSmartCutoutMode(mode) === 'advanced';
}

/** @deprecated 已改用 qwen 清晰化，保留常量避免旧引用报错 */
export const IMAGE_ENHANCE_TYPE_TABS = [
  { value: '0', label: '超分放大' },
  { value: '1', label: '变清晰' },
];

/** @deprecated 已去除放大倍数 UI；历史任务展示仍可能引用 */
export const IMAGE_ENHANCE_MULTIPLE_OPTIONS = [
  { value: 1, label: '1 倍' },
  { value: 2, label: '2 倍' },
  { value: 4, label: '4 倍' },
];

/** @deprecated 已去除质量档 UI */
export const IMAGE_ENHANCE_QUALITY_OPTIONS = [
  { value: 'auto', label: '自动' },
  { value: 'fast', label: '快速' },
  { value: 'quality', label: '高质' },
];

/** @deprecated 旧变清晰模式的 1K/2K/4K；仅兼容历史任务展示 */
export const IMAGE_ENHANCE_RESOLUTION_OPTIONS = [
  { value: 1, label: '1K' },
  { value: 2, label: '2K' },
  { value: 4, label: '4K' },
];

export const IMAGE_ENHANCE_MAX_EDGE_PX = 2048;

/** 超分提质无前端参数；清晰化提示词与模型参数由后端固定下发 */
export const IMAGE_ENHANCE_DEFAULT_PARAMS = {};

const IMAGE_ENHANCE_SPEC_ITEMS = [
  IPE_UPLOAD_FORMAT_HINT,
  `单张不超过 50MB，宽或高不超过 ${IMAGE_ENHANCE_MAX_EDGE_PX}px`,
  '可批量拖拽或粘贴上传，任务将排队处理',
  '支持选择或拖拽单个文件夹（仅读取根目录图片，最多 500 张）',
];

/** @deprecated */
export function normalizeImageEnhanceMultiple(raw) {
  const n = Number(raw);
  return [1, 2, 4].includes(n) ? n : 2;
}

/** @deprecated */
export function normalizeImageEnhanceQuality(raw) {
  const q = String(raw || '').trim().toLowerCase();
  return ['auto', 'fast', 'quality'].includes(q) ? q : 'auto';
}

/** @deprecated */
export function normalizeImageEnhanceType(raw) {
  return String(raw ?? '0').trim() === '1' ? '1' : '0';
}

/** @deprecated */
export function isImageEnhanceClarityMode(type) {
  return normalizeImageEnhanceType(type) === '1';
}

export function validateImageEnhanceDimensions(width, height) {
  const w = Number(width);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h)) return null;
  if (w > IMAGE_ENHANCE_MAX_EDGE_PX || h > IMAGE_ENHANCE_MAX_EDGE_PX) {
    return `图片宽或高不能超过 ${IMAGE_ENHANCE_MAX_EDGE_PX}px`;
  }
  return null;
}

export const STAMP_EXTRACT_PROMPT_MAX_LENGTH = 500;

export const STAMP_EXTRACT_DEFAULT_PARAMS = {
  prompt: '',
};

const STUDIO_CONFIGS = {
  imageEnhance: {
    title: '超分提质',
    subtitle: 'AI 清晰化提质',
    taskType: 'imageEnhance',
    taskName: '超分提质',
    generateLabel: '立即提质',
    generatingLabel: '提交队列中…',
    resultTitle: '提质结果',
    resultAltPrefix: '提质结果',
    legalNote: '基于 qwen-image-2.0 清晰化处理，参数由系统统一配置',
    engine: 'qwen-image-2.0',
    historyEmptyDescription: '提质完成后，记录将自动保存在这里',
    specDesc: '对模糊或低清商品图做清晰化增强，适合商品图、印花素材。',
    specItems: IMAGE_ENHANCE_SPEC_ITEMS,
    enhanceParams: IMAGE_ENHANCE_DEFAULT_PARAMS,
    statusActionLabel: '提质',
    detailPath: (id) => `/ipe/ai-image-detail/imageEnhance/${encodeURIComponent(id)}`,
    /** 提交后打开历史记录进入详情（不直跳详情） */
    openDetailOnSubmit: false,
  },
  smartCutout: {
    title: '智能抠图',
    subtitle: '可选基础（阿里云）或高级（千问白底 + RMBG）',
    taskType: 'smartCutout',
    taskName: '智能抠图',
    generateLabel: '立即抠图',
    generatingLabel: '提交队列中…',
    resultTitle: '抠图结果',
    resultAltPrefix: '抠图结果',
    legalNote: '基础：阿里云智能分割；高级：千问白底成图 + BRIA RMBG-1.4 去白底',
    engine: 'ipe-cutout',
    cutoutParams: SMART_CUTOUT_DEFAULT_PARAMS,
    historyEmptyDescription: '抠图完成后，记录将自动保存在这里',
    specDesc: '自动识别商品或图案主体并去除背景，输出透明 PNG。',
    specItems: SHARED_SPEC_ITEMS,
    statusActionLabel: '抠图',
    detailPath: (id) => `/ipe/ai-image-detail/smartCutout/${encodeURIComponent(id)}`,
  },
  stampExtract: {
    title: '印花提取',
    subtitle: '基于 qwen-image-2.0，从商品图中精准提取印花图案素材',
    taskType: 'stampExtract',
    taskName: '印花提取',
    generateLabel: '立即提取',
    generatingLabel: '提交队列中…',
    resultTitle: '提取结果',
    resultAltPrefix: '印花提取结果',
    legalNote: '基于 IPE智能工具 提取印花，自动去除背景输出透明 PNG',
    engine: 'qwen-image-2.0',
    uploadHint: '推荐上传仅包含印花区域的图片（如印花特写、平铺图），提取效果更佳',
    promptLabel: '提取指令',
    promptPlaceholder: '例如：仅提取胸前的卡通图案，保留原配色与文字；或：提取后背大面积印花，去除模特与背景…',
    promptMaxLength: STAMP_EXTRACT_PROMPT_MAX_LENGTH,
    stampParams: STAMP_EXTRACT_DEFAULT_PARAMS,
    historyEmptyDescription: '印花提取完成后，记录将自动保存在这里',
    specDesc: '识别商品图中的印花、刺绣、Logo 等装饰元素并去除背景，输出透明 PNG 素材；可填写自定义提取指令以细化需求。',
    specItems: [
      ...SHARED_SPEC_ITEMS,
      '推荐上传仅包含印花区域的图片，提取效果更佳',
      '可选填写提取指令，补充说明要提取的区域或图案类型',
    ],
    statusActionLabel: '提取',
    detailPath: (id) => `/ipe/ai-image-detail/stampExtract/${encodeURIComponent(id)}`,
  },
};

export const IMAGE_PROCESS_STUDIO_NAV_IDS = Object.keys(STUDIO_CONFIGS);

export function getImageProcessStudioConfig(navId) {
  const base = STUDIO_CONFIGS[navId];
  if (!base) return null;
  const images = NAV_HUB_CARD_IMAGES[navId] || {};
  const heroImage = images.result || images.source || NAV_HUB_CARD_IMAGES.imageCrop?.result;
  const sourceImage = images.source || heroImage;
  return {
    navId,
    maxSources: IMAGE_PROCESS_MAX_SOURCES,
    ...base,
    hero: {
      image: heroImage,
      caption: base.specDesc,
    },
    tryExamples: [
      { id: 'demo-1', thumb: sourceImage, sampleUrl: sourceImage },
      { id: 'demo-2', thumb: heroImage, sampleUrl: sourceImage },
    ],
  };
}

export function isImageProcessStudioNav(navId) {
  return IMAGE_PROCESS_STUDIO_NAV_IDS.includes(navId);
}
